import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { useAuth } from '../../hooks/useAuth';
import { useFirebase } from '../../hooks/useFirebase';
import { useSurveys } from '../../hooks/useSurveys';
import SurveyBuilder, { SurveyBuilderRef } from './SurveyBuilder';
import ConfirmDialog from '../common/ConfirmDialog';
import ResponseList from '../analytics/ResponseList';
import ChartInsights from '../analytics/ChartInsights';
import PDFExportModal from '../analytics/PDFExportModal';
import {
  SurveyResponse,
  AggregatedAnalytics,
  ExportFilter,
  SurveyDefinition,
  SurveyTemplate,
  TemplateCategory,
} from '../../types/survey.types';
import { AnalyticsPDFExporter, PDFExportOptions, AnalyticsData } from '../../utils/pdfExport';
import { generateInsights, QuestionAnalyticsInput } from '../../utils/analyticsInsights';
import { useResponses } from '../../hooks/useResponses';
import { useTemplates } from '../../hooks/useTemplates';
import TemplateCard from './TemplateCard';
import CreateFromTemplateModal from './CreateFromTemplateModal';
import SaveAsTemplateModal from './SaveAsTemplateModal';
import TemplatePreviewModal from './TemplatePreviewModal';
import * as QRCode from 'qrcode';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../utils/firebase';
import { stripHTML } from '../../utils/stripHTML';
import { SurveyStatusAction } from '../../hooks/useSurveys';
import {
  getSurveyState,
  availableActions,
  actionLabel,
  stateLabel,
  SurveyState,
} from '../../utils/surveyState';
import { APIResponse } from '../../types/survey.types';
import { formatShortDate, daysBetween } from '../../utils/dateFormatting';

interface DashboardState {
  activeSection: 'overview' | 'surveys' | 'builder' | 'templates' | 'users' | 'settings';
  surveyResponses: SurveyResponse[];
  analytics: AggregatedAnalytics | null;
  surveys: SurveyDefinition[];
  activeSurvey: SurveyDefinition | null;
  filters: ExportFilter;
  showQRModal: boolean;
  qrCodeUrl: string;
  editingSurvey: Partial<SurveyDefinition> | null;
  showSurveyBuilder: boolean;
  deleteConfirmation: {
    isOpen: boolean;
    surveyId: string | null;
    surveyTitle: string | null;
  };
  selectedSurveyForDetails: SurveyDefinition | null;
  detailsTab: 'overview' | 'responses' | 'analytics' | null;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile, loading: authLoading, logout, isAdmin, isSuperAdmin } = useAuth();
  const { getResponses } = useResponses();

  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  const [, setExporting] = useState(false);

  const [dashboardState, setDashboardState] = useState<DashboardState>({
    activeSection: 'overview',
    surveyResponses: [],
    analytics: null,
    surveys: [],
    activeSurvey: null,
    filters: {},
    showQRModal: false,
    qrCodeUrl: '',
    editingSurvey: null,
    showSurveyBuilder: false,
    deleteConfirmation: {
      isOpen: false,
      surveyId: null,
      surveyTitle: null,
    },
    selectedSurveyForDetails: null,
    detailsTab: 'overview'
  });

  // Settings page state
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState<{ updated: number; scanned: number } | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);

  // Survey list search and filter state
  const [surveySearch, setSurveySearch] = useState('');
  const [surveyStatusFilter, setSurveyStatusFilter] = useState<'all' | 'active' | 'inactive' | 'archived'>('all');
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [managingTags, setManagingTags] = useState<{ surveyId: string; surveyTitle: string; currentTags: string[] } | null>(null);

  // Ref to access SurveyBuilder methods
  const surveyBuilderRef = useRef<SurveyBuilderRef>(null);

  const { loading, error } = useFirebase();
  const { createSurvey, updateSurvey, listSurveys, deleteSurvey, duplicateSurvey, archiveSurvey, unarchiveSurvey, setSurveyStatus, loading: surveysLoading } = useSurveys();
  const {
    listTemplates,
    createTemplate,
    saveAsTemplate,
    createSurveyFromTemplate,
    deleteTemplate,
    updateTemplate,
    seedSystemTemplates,
    loading: templatesLoading,
  } = useTemplates();

  // Templates state
  const [templates, setTemplates] = useState<SurveyTemplate[]>([]);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<TemplateCategory | 'all'>('all');
  const [showCreateFromTemplate, setShowCreateFromTemplate] = useState<SurveyTemplate | null>(null);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState<{ surveyId: string; title: string; description?: string } | null>(null);
  const [showTemplatePreview, setShowTemplatePreview] = useState<SurveyTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<SurveyTemplate | null>(null);

  // Load data when user is authenticated
  useEffect(() => {
    if (user && profile) {
      loadDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile]);

  // Handle URL query parameters for navigation (enables browser back/forward)
  useEffect(() => {
    const section = searchParams.get('section') as DashboardState['activeSection'] | null;

    // If no section in URL, set initial state (replace to avoid extra history entry)
    if (!section) {
      setSearchParams({ section: 'overview' }, { replace: true });
      return;
    }

    // Handle builder section
    if (section === 'builder') {
      setDashboardState(prev => ({
        ...prev,
        activeSection: 'builder',
        showSurveyBuilder: true,
        editingSurvey: null,
        selectedSurveyForDetails: null,
        detailsTab: null
      }));
      return;
    }

    // Handle section navigation from URL
    if (section && ['overview', 'surveys', 'builder', 'templates', 'users', 'settings'].includes(section)) {
      // Regular section navigation (only update if different from current state)
      if (dashboardState.activeSection !== section) {
        setDashboardState(prev => ({
          ...prev,
          activeSection: section as DashboardState['activeSection'],
          selectedSurveyForDetails: null,
          detailsTab: null
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dashboardState.surveys]);

  // Close action menu when clicking outside
  useEffect(() => {
    if (!openActionMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openActionMenu]);

  // Update URL when dashboard state changes (for browser history)
  const updateURLParams = (section: string, surveyId?: string | null, tab?: string | null) => {
    const params: Record<string, string> = { section };
    if (surveyId) params.surveyId = surveyId;
    if (tab) params.tab = tab;
    setSearchParams(params, { replace: false });
  };

  const loadDashboardData = async () => {
    try {
      // Load surveys (including archived) and templates in parallel
      const [surveysResult, templatesResult] = await Promise.all([
        listSurveys({ includeArchived: true }),
        listTemplates().catch(() => [] as SurveyTemplate[]),
      ]);

      if (surveysResult && Array.isArray(surveysResult)) {
        setDashboardState(prev => ({
          ...prev,
          surveys: surveysResult
        }));
      } else {
        console.error('[Dashboard] Failed to load surveys: Invalid response');
        alert('Failed to load surveys. Please refresh the page.');
        setDashboardState(prev => ({ ...prev, surveys: [] }));
        return;
      }

      if (Array.isArray(templatesResult)) {
        setTemplates(templatesResult);
      }
    } catch (err) {
      console.error('[Dashboard] Error loading dashboard data:', err);
      alert('An error occurred while loading the dashboard. Please refresh the page.');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handlePDFExport = async (options: PDFExportOptions) => {
    if (!dashboardState.selectedSurveyForDetails) return;

    try {
      setExporting(true);
      console.log('[Dashboard] Generating PDF report...');

      const survey = dashboardState.selectedSurveyForDetails;
      const responses = await getResponses(survey.id);

      // Transform survey data to AnalyticsData format
      const questionAnalytics: any[] = [];

      survey.sections.forEach(section => {
        section.questions.forEach(question => {
          const questionResponses = responses
            .map(r => r.responses[question.id])
            .filter(r => r !== undefined && r !== null);

          if (questionResponses.length === 0) return;

          const analytics: any = {
            questionId: question.id,
            questionTitle: question.title,
            questionType: question.type,
            responseCount: questionResponses.length,
            totalSurveyRespondents: responses.length,
          };

          // Calculate distribution for choice-based questions
          if (['radio', 'checkbox', 'dropdown', 'yesno'].includes(question.type)) {
            const distribution: Record<string, { count: number; percentage: number; percentageOfTotal: number }> = {};

            questionResponses.forEach(response => {
              const values = Array.isArray(response) ? response : [response];
              values.forEach(value => {
                // Properly extract string value from various formats
                let key: string;
                if (typeof value === 'string') {
                  key = value;
                } else if (typeof value === 'number' || typeof value === 'boolean') {
                  key = String(value);
                } else if (value && typeof value === 'object') {
                  // Handle object responses - try common properties
                  if ('label' in value) key = String((value as any).label);
                  else if ('value' in value) key = String((value as any).value);
                  else if ('text' in value) key = String((value as any).text);
                  else key = JSON.stringify(value);
                } else {
                  key = String(value);
                }

                if (!distribution[key]) {
                  distribution[key] = { count: 0, percentage: 0, percentageOfTotal: 0 };
                }
                distribution[key].count++;
              });
            });

            Object.keys(distribution).forEach(key => {
              distribution[key].percentage = (distribution[key].count / questionResponses.length) * 100;
              distribution[key].percentageOfTotal = (distribution[key].count / responses.length) * 100;
            });

            analytics.distribution = distribution;
          }

          // Calculate numeric stats
          if (['number', 'rating', 'scale'].includes(question.type)) {
            const numericValues = questionResponses
              .map(r => {
                // Handle object responses
                if (r && typeof r === 'object') {
                  if ('value' in r) return Number((r as any).value);
                  if ('number' in r) return Number((r as any).number);
                }
                return Number(r);
              })
              .filter(n => !isNaN(n) && n !== null && n !== undefined);

            if (numericValues.length > 0) {
              const sorted = numericValues.sort((a, b) => a - b);
              analytics.numericStats = {
                min: Math.min(...numericValues),
                max: Math.max(...numericValues),
                average: numericValues.reduce((a, b) => a + b, 0) / numericValues.length,
                median: sorted[Math.floor(sorted.length / 2)]
              };
            }
          }

          // Also check if text/textarea fields contain only numbers - treat as numeric for stats
          if (['text', 'textarea'].includes(question.type)) {
            const potentialNumbers = questionResponses
              .map(r => {
                let val = r;
                if (r && typeof r === 'object') {
                  if ('value' in r) val = (r as any).value;
                  else if ('text' in r) val = (r as any).text;
                }
                return Number(val);
              })
              .filter(n => !isNaN(n) && n !== null && n !== undefined);

            // If all responses are numeric, add numeric stats
            if (potentialNumbers.length === questionResponses.length && potentialNumbers.length > 0) {
              const sorted = potentialNumbers.sort((a, b) => a - b);
              analytics.numericStats = {
                min: Math.min(...potentialNumbers),
                max: Math.max(...potentialNumbers),
                average: potentialNumbers.reduce((a, b) => a + b, 0) / potentialNumbers.length,
                median: sorted[Math.floor(sorted.length / 2)]
              };
              // Don't show as text responses if they're all numbers
              analytics.textResponses = undefined;
            }
          }

          // Dosing schedule analytics - extract distribution by days on/off pattern
          if (question.type === 'dosing_schedule') {
            const distribution: Record<string, { count: number; percentage: number; percentageOfTotal: number }> = {};
            const daysOnValues: number[] = [];

            questionResponses.forEach(r => {
              if (r && typeof r === 'object' && 'daysOn' in r) {
                const ds = r as any;
                daysOnValues.push(Number(ds.daysOn));
                const freq = ds.frequencyPerDay || 1;
                const key = `${ds.daysOn} days on / ${ds.daysOff} days off, ${freq}x daily, ${ds.cycleDuration} ${ds.cycleDurationUnit}`;
                if (!distribution[key]) distribution[key] = { count: 0, percentage: 0, percentageOfTotal: 0 };
                distribution[key].count++;
              }
            });

            Object.keys(distribution).forEach(key => {
              distribution[key].percentage = (distribution[key].count / questionResponses.length) * 100;
              distribution[key].percentageOfTotal = (distribution[key].count / responses.length) * 100;
            });
            analytics.distribution = distribution;

            if (daysOnValues.length > 0) {
              const sorted = [...daysOnValues].sort((a, b) => a - b);
              analytics.numericStats = {
                min: Math.min(...daysOnValues),
                max: Math.max(...daysOnValues),
                average: daysOnValues.reduce((a, b) => a + b, 0) / daysOnValues.length,
                median: sorted[Math.floor(sorted.length / 2)]
              };
            }

            // Also collect as text for display
            analytics.textResponses = questionResponses.map(r => {
              if (r && typeof r === 'object' && 'daysOn' in r) {
                const ds = r as any;
                const freq = ds.frequencyPerDay || 1;
                let text = `${ds.daysOn} days on, ${ds.daysOff} days off, ${freq}x daily for ${ds.cycleDuration} ${ds.cycleDurationUnit}`;
                if (freq === 1 && ds.doseAmount) {
                  text += ` at ${ds.doseAmount} ${ds.doseUnit || ''}`;
                } else if (freq > 1 && Array.isArray(ds.doseAmounts)) {
                  const doses = ds.doseAmounts.filter((d: any) => d.amount > 0).map((d: any) => `${d.label}: ${d.amount} ${ds.doseUnit || ''}`).join(', ');
                  if (doses) text += ` (${doses})`;
                }
                return text;
              }
              return JSON.stringify(r);
            });
          }

          // Measurement analytics - extract numeric stats by amount
          if (question.type === 'measurement') {
            const amounts = questionResponses
              .map(r => r && typeof r === 'object' && 'amount' in r ? Number(r.amount) : NaN)
              .filter(n => !isNaN(n));

            if (amounts.length > 0) {
              const sorted = [...amounts].sort((a, b) => a - b);
              analytics.numericStats = {
                min: Math.min(...amounts),
                max: Math.max(...amounts),
                average: amounts.reduce((a, b) => a + b, 0) / amounts.length,
                median: sorted[Math.floor(sorted.length / 2)]
              };
            }

            analytics.textResponses = questionResponses.map(r => {
              if (r && typeof r === 'object' && 'amount' in r) {
                const m = r as any;
                return `${m.amount} ${m.unit || ''}`;
              }
              return JSON.stringify(r);
            });
          }

          // Collect text responses
          if (['text', 'textarea', 'email'].includes(question.type)) {
            analytics.textResponses = questionResponses.map(r => {
              // Handle various response formats
              if (typeof r === 'string') return r;
              if (typeof r === 'number') return String(r);
              if (r && typeof r === 'object') {
                if ('value' in r) return String((r as any).value);
                if ('text' in r) return String((r as any).text);
              }
              // Fallback: try to extract meaningful string
              return JSON.stringify(r);
            });
          }

          questionAnalytics.push(analytics);
        });
      });

      // Build AnalyticsData structure
      const analyticsData: AnalyticsData = {
        totalResponses: responses.length,
        completionRate: survey.analytics?.completionRate || 100,
        avgCompletionTime: survey.analytics?.averageCompletionTime || 0,
        sections: survey.sections.map(section => ({
          sectionId: section.id,
          sectionTitle: section.title,
          questions: questionAnalytics.filter(qa =>
            section.questions.some(q => q.id === qa.questionId)
          )
        }))
      };

      // Generate AI insights from analytics data
      const insightsInput: QuestionAnalyticsInput[] = questionAnalytics.map(qa => ({
        questionId: qa.questionId,
        questionTitle: qa.questionTitle,
        questionType: qa.questionType,
        responseCount: qa.responseCount,
        totalSurveyRespondents: qa.totalSurveyRespondents,
        eligibleRespondents: qa.eligibleRespondents,
        skipCount: qa.skipCount,
        distribution: qa.distribution ? Object.fromEntries(
          (Object.entries(qa.distribution) as [string, { count: number; percentage: number; percentageOfTotal: number }][]).map(([key, val]) => [key, {
            count: val.count,
            percentOfAnswered: val.percentage,
            percentOfTotal: val.percentageOfTotal,
          }])
        ) : undefined,
        numericStats: qa.numericStats,
        textResponses: qa.textResponses?.map((text: string, idx: number) => ({
          text,
          wordCount: text.split(/\s+/).filter((w: string) => w.length > 0).length,
          responseIndex: idx,
        })),
      }));
      const insights = generateInsights(insightsInput, responses.length);

      // Generate PDF with layout options passed to constructor
      const exporter = new AnalyticsPDFExporter(options);
      const pdfBlob = await exporter.generateReport(analyticsData, options, insights);

      // Download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${survey.title.replace(/[^a-z0-9]/gi, '_')}_Analytics_Report.pdf`;
      link.click();
      URL.revokeObjectURL(url);

      console.log('[Dashboard] PDF report generated successfully');
      setShowPDFExportModal(false);
    } catch (error) {
      console.error('[Dashboard] PDF export error:', error);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const generateQRCode = async (surveyId: string) => {
    try {
      // Find the survey
      const survey = dashboardState.surveys.find(s => s.id === surveyId);

      if (!survey) {
        alert('Survey not found');
        return;
      }

      // Access code should already exist (auto-generated on creation)
      if (!survey.accessCode) {
        console.error('Survey missing access code:', surveyId);
        alert('This survey is missing an access code. Please contact support.');
        return;
      }

      // Use production URL for QR codes
      const baseUrl = 'https://criscicreate.com.au';
      const surveyUrl = `${baseUrl}/survey/qr/${surveyId}`;

      const qrCodeDataUrl = await QRCode.toDataURL(surveyUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      setDashboardState(prev => ({
        ...prev,
        qrCodeUrl: qrCodeDataUrl,
        showQRModal: true,
        activeSurvey: survey
      }));
    } catch (err) {
      console.error('QR code generation error:', err);
      alert('Failed to generate QR code. Please try again.');
    }
  };

  const handleDeleteSurveyClick = (surveyId: string, surveyTitle: string) => {
    setDashboardState(prev => ({
      ...prev,
      deleteConfirmation: {
        isOpen: true,
        surveyId,
        surveyTitle,
      }
    }));
  };

  const handleDeleteSurveyConfirm = async () => {
    const { surveyId } = dashboardState.deleteConfirmation;
    if (!surveyId) return;

    try {
      await deleteSurvey(surveyId);
      alert('Survey deleted successfully!');

      // Reload surveys
      await loadDashboardData();

      // Close dialog
      setDashboardState(prev => ({
        ...prev,
        deleteConfirmation: {
          isOpen: false,
          surveyId: null,
          surveyTitle: null,
        }
      }));
    } catch (err: any) {
      console.error('Error deleting survey:', err);
      alert(`Failed to delete survey: ${err.message}`);
    }
  };

  const handleDeleteSurveyCancel = () => {
    setDashboardState(prev => ({
      ...prev,
      deleteConfirmation: {
        isOpen: false,
        surveyId: null,
        surveyTitle: null,
      }
    }));
  };

  /**
   * Handler: Archive Survey
   */
  const handleArchiveSurvey = async (surveyId: string, surveyTitle: string) => {
    if (!window.confirm(`Archive "${surveyTitle}"? This will deactivate the survey and hide it from the main list.`)) {
      return;
    }

    try {
      await archiveSurvey(surveyId);
      alert('Survey archived successfully!');
      await loadDashboardData();
    } catch (err: any) {
      console.error('Error archiving survey:', err);
      alert(`Failed to archive survey: ${err.message}`);
    }
  };

  /**
   * Handler: Unarchive Survey
   */
  const handleUnarchiveSurvey = async (surveyId: string) => {
    try {
      await unarchiveSurvey(surveyId);
      alert('Survey unarchived successfully! It is now inactive — activate it when ready.');
      await loadDashboardData();
    } catch (err: any) {
      console.error('Error unarchiving survey:', err);
      alert(`Failed to unarchive survey: ${err.message}`);
    }
  };

  const handleDuplicateSurvey = async (surveyId: string, surveyTitle: string) => {
    try {
      const newTitle = `${surveyTitle} (Copy)`;
      const newSurveyId = await duplicateSurvey(surveyId, newTitle);

      // Reload surveys to get the new duplicated survey
      await loadDashboardData();

      // Find the newly duplicated survey in the updated list
      const newSurveys = await listSurveys();
      const duplicatedSurvey = newSurveys.find(s => s.id === newSurveyId);

      if (duplicatedSurvey) {
        // Automatically open the duplicated survey in the builder
        setDashboardState(prev => ({
          ...prev,
          activeSection: 'builder',
          editingSurvey: duplicatedSurvey,
          showSurveyBuilder: true
        }));
      } else {
        alert(`Survey duplicated successfully! New survey ID: ${newSurveyId}`);
      }
    } catch (err: any) {
      console.error('Error duplicating survey:', err);
      alert(`Failed to duplicate survey: ${err.message}`);
    }
  };

  /**
   * Handler: Edit Survey
   * Opens the survey builder with the selected survey loaded for editing
   */
  const handleEditSurvey = (surveyId: string) => {
    const survey = dashboardState.surveys.find(s => s.id === surveyId);
    if (!survey) {
      alert('Survey not found');
      return;
    }

    setDashboardState(prev => ({
      ...prev,
      activeSection: 'builder',
      editingSurvey: survey,
      showSurveyBuilder: true
    }));
  };

  /**
   * Handler: Lifecycle action (activate / pause / resume / close / reopen).
   * Server validates the transition and stamps launchedAt / closedAt as appropriate.
   * Confirms before Close (terminal) since it requires an explicit Reopen to undo.
   */
  const handleLifecycleAction = async (surveyId: string, action: SurveyStatusAction) => {
    try {
      if (action === 'close') {
        const ok = window.confirm(
          'Close this survey? Responses can still be viewed, but no new responses will be accepted until you Reopen it.'
        );
        if (!ok) return;
      }

      await setSurveyStatus(surveyId, action);
      await loadDashboardData();
    } catch (error) {
      console.error(`Error during survey ${action}:`, error);
      alert(`Failed to ${action} survey. Please try again.`);
    }
  };

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--gradient-page)' }}>
        <div className="glass-card text-center" style={{ padding: '2rem 3rem' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: 'var(--color-accent)' }}></div>
          <p className="mt-4" style={{ color: 'var(--color-primary)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  // SVG Icons for navigation
  const NavIcons = {
    overview: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    surveys: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    builder: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
      </svg>
    ),
    users: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    settings: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    templates: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
      </svg>
    ),
    studies: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    )
  };

  // Navigation sections - filter based on user role (removed 'data' section)
  const allNavigationSections = [
    { id: 'overview', name: 'Overview', icon: NavIcons.overview, description: 'Dashboard overview and statistics', requiredRole: 'viewer' as const },
    { id: 'surveys', name: 'Surveys', icon: NavIcons.surveys, description: 'Manage surveys, view responses and analytics', requiredRole: 'admin' as const },
    { id: 'builder', name: 'Survey Builder', icon: NavIcons.builder, description: 'Create and edit surveys', requiredRole: 'admin' as const },
    { id: 'templates', name: 'Templates', icon: NavIcons.templates, description: 'Reusable survey templates', requiredRole: 'admin' as const },
    { id: 'users', name: 'Users', icon: NavIcons.users, description: 'Manage admin users', requiredRole: 'admin' as const },
    { id: 'settings', name: 'Settings', icon: NavIcons.settings, description: 'System settings and configuration', requiredRole: 'viewer' as const }
  ];

  // Filter navigation based on role
  const navigationSections = allNavigationSections.filter(section => {
    if (section.requiredRole === 'admin') {
      return isAdmin || isSuperAdmin;
    }
    return true; // viewer can see everything with requiredRole: 'viewer'
  });

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Action Button */}
      <div className="flex justify-end items-center">
        <button
          onClick={() => setDashboardState(prev => ({ ...prev, activeSection: 'builder' }))}
          className="glass-btn-primary glass-btn-sm"
        >
          Create Survey
        </button>
      </div>

      {/* Stats Grid - Refined and Compact */}
      <div className="grid grid-cols-4 gap-3">
        <div className="glass-card-sm">
          <p className="text-muted text-xs font-medium uppercase mb-1" style={{ letterSpacing: 'var(--letter-spacing-caps)' }}>
            Surveys
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.75rem', fontWeight: 600, color: 'var(--color-primary)', lineHeight: '1' }}>
            {dashboardState.surveys.length}
          </p>
        </div>

        <div className="glass-card-sm">
          <p className="text-muted text-xs font-medium uppercase mb-1" style={{ letterSpacing: 'var(--letter-spacing-caps)' }}>
            Responses
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.75rem', fontWeight: 600, color: 'var(--color-primary)', lineHeight: '1' }}>
            {dashboardState.surveys.reduce((sum, survey) => sum + survey.analytics.totalResponses, 0)}
          </p>
        </div>

        <div className="glass-card-sm">
          <p className="text-muted text-xs font-medium uppercase mb-1" style={{ letterSpacing: 'var(--letter-spacing-caps)' }}>
            Active
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.75rem', fontWeight: 600, color: 'var(--color-accent)', lineHeight: '1' }}>
            {dashboardState.surveys.filter(s => s.settings.isActive).length}
          </p>
        </div>

        <div className="glass-card-sm">
          <p className="text-muted text-xs font-medium uppercase mb-1" style={{ letterSpacing: 'var(--letter-spacing-caps)' }}>
            Completion
          </p>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.75rem', fontWeight: 600, color: 'var(--color-primary)', lineHeight: '1' }}>
            {Math.round(dashboardState.surveys.reduce((sum, survey) => sum + survey.analytics.completionRate, 0) / dashboardState.surveys.length) || 0}%
          </p>
        </div>
      </div>

      {/* Quick Actions - Single Line Only */}
      <div className="grid grid-cols-3 gap-3">
        <button
          onClick={() => setDashboardState(prev => ({ ...prev, activeSection: 'builder' }))}
          className="glass-card-interactive text-center"
          style={{ padding: '0.875rem' }}
        >
          <h4 className="text-sm" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--color-primary)' }}>Create Survey</h4>
        </button>

        <button
          onClick={() => setDashboardState(prev => ({ ...prev, activeSection: 'surveys' }))}
          className="glass-card-interactive text-center"
          style={{ padding: '0.875rem' }}
        >
          <h4 className="text-sm" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--color-primary)' }}>Manage Surveys</h4>
        </button>

        <button
          onClick={() => setDashboardState(prev => ({ ...prev, activeSection: 'surveys' }))}
          className="glass-card-interactive text-center"
          style={{ padding: '0.875rem' }}
        >
          <h4 className="text-sm" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, color: 'var(--color-primary)' }}>View Survey Data</h4>
        </button>
      </div>

      {/* Recent Surveys - Refined */}
      <div className="glass-card" style={{ padding: '1rem' }}>
        <h3 className="text-xs font-medium uppercase mb-3 text-muted" style={{ letterSpacing: 'var(--letter-spacing-caps)' }}>
          Recent Surveys
        </h3>
        <div className="space-y-2">
          {dashboardState.surveys.slice(0, 3).map((survey) => (
            <div
              key={survey.id}
              className="flex items-center justify-between p-2.5 rounded-lg transition-all glass-card-sm"
            >
              <div className="flex items-center gap-2.5 flex-1 min-w-0">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: survey.settings.isActive ? 'var(--color-success)' : 'var(--color-muted-light)' }}
                ></div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-medium text-sm truncate" style={{ color: 'var(--color-primary)' }}>
                    {survey.title}
                  </h4>
                  <p className="text-xs text-muted">{survey.analytics.totalResponses} responses</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => generateQRCode(survey.id)}
                  className="glass-btn-outline glass-btn-sm"
                >
                  QR
                </button>
                <button
                  onClick={() => handleEditSurvey(survey.id)}
                  className="glass-btn-ghost glass-btn-sm"
                  title="Edit this survey"
                >
                  Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Compute all unique tags from loaded surveys
  const allTags = Array.from(new Set(dashboardState.surveys.flatMap(s => s.tags || []))).sort();

  // Filter surveys based on search, status, and tags
  const filteredSurveys = dashboardState.surveys.filter(survey => {
    const matchesSearch = surveySearch === '' ||
      survey.title.toLowerCase().includes(surveySearch.toLowerCase()) ||
      (survey.description && survey.description.toLowerCase().includes(surveySearch.toLowerCase()));

    const isArchived = survey.isArchived === true;
    const matchesStatus =
      (surveyStatusFilter === 'all' && !isArchived) ||
      (surveyStatusFilter === 'active' && survey.settings?.isActive && !isArchived) ||
      (surveyStatusFilter === 'inactive' && !survey.settings?.isActive && !isArchived) ||
      (surveyStatusFilter === 'archived' && isArchived);

    const matchesTags = selectedTagFilters.length === 0 ||
      selectedTagFilters.some(tag => survey.tags?.includes(tag));

    return matchesSearch && matchesStatus && matchesTags;
  });

  const renderSurveys = () => (
    <div className="space-y-4">
      {/* Header with Search and Filters */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-md relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search surveys..."
            value={surveySearch}
            onChange={(e) => setSurveySearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border transition-colors text-sm"
            style={{
              backgroundColor: 'white',
              borderColor: 'var(--glass-border-light)',
              color: 'var(--color-primary)'
            }}
          />
        </div>

        {/* Filter Buttons */}
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--glass-border-light)' }}>
            {(['all', 'active', 'inactive', 'archived'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setSurveyStatusFilter(status)}
                className="px-3 py-1.5 text-xs font-medium transition-colors capitalize"
                style={{
                  backgroundColor: surveyStatusFilter === status ? 'var(--color-accent)' : 'white',
                  color: surveyStatusFilter === status ? 'var(--color-primary)' : 'var(--color-muted)'
                }}
              >
                {status} {status === 'all' ? `(${dashboardState.surveys.filter(s => !s.isArchived).length})` :
                  status === 'active' ? `(${dashboardState.surveys.filter(s => s.settings?.isActive && !s.isArchived).length})` :
                  status === 'inactive' ? `(${dashboardState.surveys.filter(s => !s.settings?.isActive && !s.isArchived).length})` :
                  `(${dashboardState.surveys.filter(s => s.isArchived).length})`}
              </button>
            ))}
          </div>

          <button
            onClick={() => setDashboardState(prev => ({ ...prev, activeSection: 'builder' }))}
            className="glass-btn-primary glass-btn-sm flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>New Survey</span>
          </button>
        </div>
      </div>

      {/* Tag Filter Bar */}
      {allTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>Tags:</span>
          {allTags.map(tag => {
            const isSelected = selectedTagFilters.includes(tag);
            return (
              <button
                key={tag}
                onClick={() => setSelectedTagFilters(prev =>
                  isSelected ? prev.filter(t => t !== tag) : [...prev, tag]
                )}
                className="text-xs px-2 py-0.5 rounded-full transition-colors"
                style={{
                  backgroundColor: isSelected ? 'rgba(38, 67, 114, 0.12)' : 'rgba(38, 67, 114, 0.04)',
                  color: isSelected ? '#264372' : 'var(--color-muted)',
                  border: isSelected ? '1px solid rgba(38, 67, 114, 0.3)' : '1px solid rgba(38, 67, 114, 0.1)',
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                {tag}
              </button>
            );
          })}
          {selectedTagFilters.length > 0 && (
            <button
              onClick={() => setSelectedTagFilters([])}
              className="text-xs px-2 py-0.5 transition-colors hover:underline"
              style={{ color: 'var(--color-muted)' }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {surveysLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="glass-card p-4 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="h-5 rounded w-1/4" style={{ backgroundColor: 'var(--glass-bg-light)' }}></div>
                <div className="h-4 rounded w-16" style={{ backgroundColor: 'var(--glass-bg-light)' }}></div>
                <div className="flex-1"></div>
                <div className="h-4 rounded w-20" style={{ backgroundColor: 'var(--glass-bg-light)' }}></div>
              </div>
            </div>
          ))}
        </div>
      ) : dashboardState.surveys.length === 0 ? (
        <div className="glass-card p-12 text-center" style={{ border: '2px dashed var(--glass-border-medium)' }}>
          <div className="max-w-sm mx-auto">
            <svg className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-accent)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>No surveys yet</h3>
            <p className="mb-6 text-sm" style={{ color: 'var(--color-muted)' }}>Create your first survey to start collecting insights</p>
            <button
              onClick={() => setDashboardState(prev => ({ ...prev, activeSection: 'builder' }))}
              className="glass-btn-primary"
            >
              Create Survey
            </button>
          </div>
        </div>
      ) : filteredSurveys.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <p style={{ color: 'var(--color-muted)' }}>No surveys match your search</p>
          <button
            onClick={() => { setSurveySearch(''); setSurveyStatusFilter('all'); }}
            className="text-sm mt-2"
            style={{ color: 'var(--color-accent)' }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          {/* Compact Survey List */}
          <div className="space-y-1">
            {filteredSurveys.map((survey) => (
              <div
                key={survey.id}
                className="bg-white rounded-lg border hover:shadow-sm transition-shadow"
                style={{
                  borderColor: 'var(--glass-border-light)',
                  padding: '0.625rem 0.875rem',
                  opacity: survey.isArchived ? 0.6 : 1,
                }}
              >
                {/* Single Row Layout - Always horizontal */}
                <div className="flex items-center gap-3">
                  {/* Status Dot */}
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: survey.isArchived ? '#94a3b8' : survey.settings?.isActive ? '#16a34a' : '#9ca3af' }}
                    title={survey.isArchived ? 'Archived' : survey.settings?.isActive ? 'Active' : 'Inactive'}
                  />

                  {/* Title & Meta */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3
                        className="font-medium truncate cursor-pointer hover:underline"
                        style={{ color: 'var(--color-primary)', fontSize: '0.875rem' }}
                        onClick={() => handleEditSurvey(survey.id)}
                        title={survey.title}
                      >
                        {survey.title}
                      </h3>
                      <span className="text-xs flex-shrink-0" style={{ color: 'var(--color-muted)' }}>
                        {survey.sections?.reduce((total, section) => total + section.questions.length, 0) || 0}Q
                      </span>
                      {survey.tags && survey.tags.length > 0 && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {survey.tags.slice(0, 3).map(tag => (
                            <span
                              key={tag}
                              className="text-xs px-1.5 py-0.5 rounded"
                              style={{
                                backgroundColor: 'var(--glass-bg-card)',
                                color: 'var(--color-muted)',
                                border: '1px solid var(--glass-border-light)',
                              }}
                            >
                              {tag}
                            </span>
                          ))}
                          {survey.tags.length > 3 && (
                            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                              +{survey.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Inline Stats */}
                  <div className="flex items-center gap-4 flex-shrink-0">
                    {/* Launched / Closed dates — Kylie's lifecycle visibility */}
                    <div className="hidden md:flex flex-col items-end" style={{ minWidth: '7rem', lineHeight: 1.15 }}>
                      <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
                        {survey.closedAt ? 'Closed' : survey.launchedAt ? 'Launched' : 'Status'}
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'var(--color-primary)' }}>
                        {survey.closedAt
                          ? formatShortDate(survey.closedAt, '—')
                          : survey.launchedAt
                          ? formatShortDate(survey.launchedAt, '—')
                          : 'Draft'}
                      </span>
                    </div>
                    <div className="text-right" style={{ minWidth: '5.5rem' }}>
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
                        {survey.analytics?.totalResponses || 0}
                      </span>
                      <span className="text-xs ml-1" style={{ color: 'var(--color-muted)' }}>
                        {survey.analytics?.totalResponses === 1 ? 'Response' : 'Responses'}
                      </span>
                    </div>
                    <div className="text-center" style={{ minWidth: '3rem' }}>
                      <span className="text-sm font-semibold" style={{ color: 'var(--color-accent)' }}>
                        {survey.analytics?.completionRate || 0}%
                      </span>
                    </div>
                    <div className="hidden sm:block">
                      <span
                        className="font-mono text-xs px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: 'var(--glass-bg-light)', color: 'var(--color-primary)' }}
                      >
                        {survey.accessCode || '—'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons: Inline + Overflow Menu */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Inline actions — fixed widths for alignment */}
                    {survey.isArchived ? (
                      <button
                        onClick={() => handleUnarchiveSurvey(survey.id)}
                        className="py-1 rounded text-xs font-medium transition-colors text-center"
                        style={{
                          width: '7.25rem',
                          backgroundColor: 'rgba(38, 67, 114, 0.08)',
                          color: '#264372',
                          border: '1px solid rgba(38, 67, 114, 0.15)'
                        }}
                      >
                        Unarchive
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditSurvey(survey.id)}
                          className="py-1 rounded text-xs font-medium transition-colors text-center"
                          style={{
                            width: '3rem',
                            backgroundColor: 'rgba(38, 67, 114, 0.08)',
                            color: '#264372',
                            border: '1px solid rgba(38, 67, 114, 0.15)'
                          }}
                        >
                          Edit
                        </button>
                        {(() => {
                          const state: SurveyState = getSurveyState(survey);
                          const actions = availableActions(state);
                          const styleFor = (action: SurveyStatusAction): React.CSSProperties => {
                            // Close = terminal (warm warning tone). Activate/Resume/Reopen = accent.
                            // Pause = neutral.
                            if (action === 'close') {
                              return {
                                backgroundColor: 'rgba(199, 88, 74, 0.08)',
                                color: '#a4503e',
                                border: '1px solid rgba(199, 88, 74, 0.2)',
                              };
                            }
                            if (action === 'activate' || action === 'resume' || action === 'reopen') {
                              return {
                                backgroundColor: 'rgba(199, 161, 74, 0.1)',
                                color: '#8f6f2a',
                                border: '1px solid rgba(199, 161, 74, 0.2)',
                              };
                            }
                            return {
                              backgroundColor: 'rgba(38, 67, 114, 0.08)',
                              color: '#264372',
                              border: '1px solid rgba(38, 67, 114, 0.15)',
                            };
                          };
                          return actions.map((action) => (
                            <button
                              key={action}
                              onClick={() => handleLifecycleAction(survey.id, action)}
                              className="py-1 rounded text-xs font-medium transition-colors text-center"
                              style={{
                                width: action === 'activate' ? '4.5rem' : '3.75rem',
                                ...styleFor(action),
                              }}
                            >
                              {actionLabel(action)}
                            </button>
                          ));
                        })()}
                      </>
                    )}
                    <button
                      onClick={() => {
                        setDashboardState(prev => ({
                          ...prev,
                          selectedSurveyForDetails: survey,
                          detailsTab: 'analytics'
                        }));
                      }}
                      className="py-1 rounded text-xs font-medium transition-colors text-center"
                      style={{
                        width: '4.75rem',
                        backgroundColor: 'rgba(38, 67, 114, 0.08)',
                        color: '#264372',
                        border: '1px solid rgba(38, 67, 114, 0.15)'
                      }}
                    >
                      Analytics
                    </button>
                    <button
                      onClick={() => generateQRCode(survey.id)}
                      className="py-1 rounded text-xs font-medium transition-colors text-center"
                      style={{
                        width: '2.25rem',
                        backgroundColor: 'rgba(38, 67, 114, 0.08)',
                        color: '#264372',
                        border: '1px solid rgba(38, 67, 114, 0.15)'
                      }}
                      title="Generate QR Code"
                    >
                      QR
                    </button>

                    {/* Overflow menu */}
                    <div className="relative" ref={openActionMenu === survey.id ? actionMenuRef : undefined}>
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === survey.id ? null : survey.id)}
                        className="px-2 py-1 rounded text-xs font-medium transition-colors"
                        style={{
                          backgroundColor: openActionMenu === survey.id ? 'rgba(38, 67, 114, 0.12)' : 'rgba(38, 67, 114, 0.05)',
                          color: '#264372',
                          border: '1px solid rgba(38, 67, 114, 0.1)'
                        }}
                        title="More actions"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>
                      {openActionMenu === survey.id && (
                        <div
                          className="absolute right-0 top-full mt-1 rounded-lg border shadow-lg z-50 py-1 min-w-[160px]"
                          style={{
                            backgroundColor: 'white',
                            borderColor: 'var(--glass-border-light)',
                            boxShadow: 'var(--glass-shadow-md)'
                          }}
                        >
                          {/* Tags */}
                          <button
                            onClick={() => { setManagingTags({ surveyId: survey.id, surveyTitle: survey.title, currentTags: survey.tags || [] }); setOpenActionMenu(null); }}
                            className="w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50"
                            style={{ color: '#264372' }}
                          >
                            Manage Tags
                          </button>

                          {/* Actions group */}
                          {!survey.isArchived && (
                            <>
                              <div className="border-t my-1" style={{ borderColor: 'rgba(38, 67, 114, 0.08)' }} />
                              <button
                                onClick={() => { handleDuplicateSurvey(survey.id, survey.title); setOpenActionMenu(null); }}
                                className="w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50"
                                style={{ color: '#264372' }}
                              >
                                Duplicate
                              </button>
                              <button
                                onClick={() => { setShowSaveAsTemplate({ surveyId: survey.id, title: survey.title, description: survey.description }); setOpenActionMenu(null); }}
                                className="w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50"
                                style={{ color: '#264372' }}
                              >
                                Save as Template
                              </button>
                            </>
                          )}

                          {/* Manage group */}
                          <div className="border-t my-1" style={{ borderColor: 'rgba(38, 67, 114, 0.08)' }} />
                          {!survey.isArchived && (
                            <button
                              onClick={() => { handleArchiveSurvey(survey.id, survey.title); setOpenActionMenu(null); }}
                              className="w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-gray-50"
                              style={{ color: '#6a8caf' }}
                            >
                              Archive
                            </button>
                          )}
                          <button
                            onClick={() => { handleDeleteSurveyClick(survey.id, survey.title); setOpenActionMenu(null); }}
                            className="w-full text-left px-3 py-2 text-xs font-medium transition-colors hover:bg-red-50"
                            style={{ color: 'var(--status-error)' }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="text-xs text-center pt-2" style={{ color: 'var(--color-muted)' }}>
            {filteredSurveys.length} of {dashboardState.surveys.length} surveys
          </div>
        </>
      )}

      {/* Inline Survey Details View */}
      {dashboardState.selectedSurveyForDetails && dashboardState.detailsTab && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-8 pb-8 overflow-y-auto" style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <div>
                <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-primary)' }}>
                  {dashboardState.selectedSurveyForDetails.title}
                </h2>
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                  {dashboardState.selectedSurveyForDetails.analytics?.totalResponses || 0} responses · {dashboardState.selectedSurveyForDetails.analytics?.completionRate || 0}% completion rate
                </p>
              </div>
              <button
                onClick={() => setDashboardState(prev => ({ ...prev, selectedSurveyForDetails: null, detailsTab: null }))}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                style={{ color: 'var(--color-muted)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Lifecycle strip — Launched / Closed dates Kylie asked for */}
            {(() => {
              const s = dashboardState.selectedSurveyForDetails!;
              const state = getSurveyState(s);
              const launched = formatShortDate(s.launchedAt, '—');
              const closed = formatShortDate(s.closedAt, '—');
              const stateColor = ({ draft: '#6b7280', active: '#8f6f2a', paused: '#264372', closed: '#a4503e' } as const)[state];
              const stateBg = ({ draft: 'rgba(107,114,128,0.1)', active: 'rgba(199,161,74,0.15)', paused: 'rgba(38,67,114,0.1)', closed: 'rgba(199,88,74,0.1)' } as const)[state];

              let detail: React.ReactNode;
              if (state === 'draft') {
                detail = <span style={{ color: 'var(--color-muted)' }}>not yet launched</span>;
              } else if (state === 'active') {
                const days = s.launchedAt ? daysBetween(s.launchedAt, new Date()) : 0;
                detail = <><strong style={{ color: 'var(--color-primary)' }}>Launched</strong> {launched} <span style={{ color: 'var(--color-muted)' }}>· {days} day{days === 1 ? '' : 's'} running</span></>;
              } else if (state === 'paused') {
                detail = <><strong style={{ color: 'var(--color-primary)' }}>Launched</strong> {launched}</>;
              } else {
                const window = (s.launchedAt && s.closedAt) ? daysBetween(s.launchedAt, s.closedAt) : 0;
                detail = <><strong style={{ color: 'var(--color-primary)' }}>Launched</strong> {launched} <span style={{ color: 'var(--color-muted)' }}>→</span> <strong style={{ color: 'var(--color-primary)' }}>Closed</strong> {closed}{window > 0 && <span style={{ color: 'var(--color-muted)' }}> ({window} day{window === 1 ? '' : 's'})</span>}</>;
              }

              return (
                <div className="flex flex-wrap items-center gap-3 px-6 py-3" style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: 'rgba(38, 67, 114, 0.02)' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '0.2rem 0.55rem',
                    borderRadius: '999px',
                    backgroundColor: stateBg,
                    color: stateColor,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}>
                    {stateLabel(state)}
                  </span>
                  <span style={{ fontSize: '0.875rem', color: 'var(--color-primary)' }}>{detail}</span>
                </div>
              );
            })()}

            {/* Tabs */}
            <div className="px-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <nav className="-mb-px flex space-x-6">
                {(['overview', 'responses', 'analytics'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setDashboardState(prev => ({ ...prev, detailsTab: tab }))}
                    className="py-3 text-sm font-medium capitalize transition-colors"
                    style={{
                      borderBottom: dashboardState.detailsTab === tab ? '2px solid var(--color-accent)' : '2px solid transparent',
                      color: dashboardState.detailsTab === tab ? 'var(--color-accent)' : 'var(--color-muted)'
                    }}
                  >
                    {tab === 'analytics' ? 'Analytics & Charts' : tab === 'responses' ? 'Response Data' : 'Overview'}
                  </button>
                ))}
              </nav>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 16rem)' }}>
              {dashboardState.detailsTab === 'overview' && (
                <div className="grid grid-cols-2 gap-6">
                  <div className="glass-card p-5">
                    <h4 className="font-semibold mb-4" style={{ color: 'var(--color-primary)' }}>Survey Structure</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--color-muted)' }}>Sections</span>
                        <span className="font-medium">{dashboardState.selectedSurveyForDetails.sections.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--color-muted)' }}>Questions</span>
                        <span className="font-medium">{dashboardState.selectedSurveyForDetails.sections.reduce((sum, s) => sum + s.questions.length, 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--color-muted)' }}>Status</span>
                        <span className={`font-medium ${dashboardState.selectedSurveyForDetails.settings?.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                          {dashboardState.selectedSurveyForDetails.settings?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="glass-card p-5">
                    <h4 className="font-semibold mb-4" style={{ color: 'var(--color-primary)' }}>Performance</h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--color-muted)' }}>Total Responses</span>
                        <span className="font-medium" style={{ color: 'var(--color-accent)' }}>{dashboardState.selectedSurveyForDetails.analytics?.totalResponses || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--color-muted)' }}>Completion Rate</span>
                        <span className="font-medium">{dashboardState.selectedSurveyForDetails.analytics?.completionRate || 0}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: 'var(--color-muted)' }}>Avg Time</span>
                        <span className="font-medium">{Math.round((dashboardState.selectedSurveyForDetails.analytics?.averageCompletionTime || 0) / 60)}m</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {dashboardState.detailsTab === 'responses' && (
                <ResponseList
                  surveyId={dashboardState.selectedSurveyForDetails.id}
                  survey={dashboardState.selectedSurveyForDetails}
                />
              )}

              {dashboardState.detailsTab === 'analytics' && (
                <ChartInsights
                  surveyId={dashboardState.selectedSurveyForDetails.id}
                  survey={dashboardState.selectedSurveyForDetails}
                  onExportPDF={() => setShowPDFExportModal(true)}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const handleSaveSurvey = async (survey: Partial<SurveyDefinition>, options?: { silent?: boolean }): Promise<string | void> => {
    try {
      // Debug logging to check description value
      console.log('Saving survey with description:', survey.description);
      console.log('Description character codes:', survey.description?.split('').map((c, i) => `${i}:'${c}'(${c.charCodeAt(0)})`).join(', '));

      if (survey.id) {
        // Update existing survey
        await updateSurvey(survey.id, survey);
        if (!options?.silent) {
          alert('Survey updated successfully!');
        }
        return; // No new ID for updates
      } else {
        // Create new survey
        const newSurveyId = await createSurvey(survey);
        console.log('Survey created with ID:', newSurveyId);

        // Update the editing survey with the new ID so future saves are updates
        setDashboardState(prev => ({
          ...prev,
          editingSurvey: { ...survey, id: newSurveyId }
        }));

        if (!options?.silent) {
          alert('Survey created successfully!');
        }

        // Return the new ID so the caller can update its local state
        // This is critical to prevent duplicate surveys on autosave
        return newSurveyId;
      }
    } catch (err: any) {
      console.error('Error saving survey:', err);
      if (!options?.silent) {
        alert(`Failed to save survey: ${err.message}`);
      }
      return; // Return void on error
    } finally {
      // Reload surveys in background (don't block) - only for manual saves
      if (!options?.silent) {
        await loadDashboardData();

        // Close builder only for manual saves
        setDashboardState(prev => ({
          ...prev,
          showSurveyBuilder: false,
          editingSurvey: null
        }));
      }
    }
  };

  const handleCancelSurveyBuilder = () => {
    const wasEditingTemplate = !!editingTemplate;
    setEditingTemplate(null);
    setDashboardState(prev => ({
      ...prev,
      showSurveyBuilder: false,
      editingSurvey: null,
      ...(wasEditingTemplate ? { activeSection: 'templates' as const } : {}),
    }));
    if (wasEditingTemplate) {
      updateURLParams('templates');
    }
  };

  const startNewSurvey = () => {
    setDashboardState(prev => ({
      ...prev,
      editingSurvey: null,
      showSurveyBuilder: true
    }));
  };

  const editSurvey = (survey: SurveyDefinition) => {
    setDashboardState(prev => ({
      ...prev,
      editingSurvey: survey,
      showSurveyBuilder: true
    }));
  };

  // ========================================
  // TEMPLATES SECTION
  // ========================================

  const handleUseTemplate = (template: SurveyTemplate) => {
    setShowTemplatePreview(null);
    setShowCreateFromTemplate(template);
  };

  const handleCreateFromTemplate = async (title: string, description: string, variables?: Record<string, string>) => {
    if (!showCreateFromTemplate) return;
    try {
      const newSurveyId = await createSurveyFromTemplate(showCreateFromTemplate.id, title, description, variables);
      setShowCreateFromTemplate(null);

      // Reload surveys and open the new survey in builder
      await loadDashboardData();
      const newSurveys = await listSurveys();
      const newSurvey = newSurveys.find(s => s.id === newSurveyId);

      if (newSurvey) {
        setDashboardState(prev => ({
          ...prev,
          activeSection: 'builder',
          editingSurvey: newSurvey,
          showSurveyBuilder: true,
        }));
        updateURLParams('builder');
      }
    } catch (err: any) {
      alert(`Failed to create survey from template: ${err.message}`);
    }
  };

  const handleSaveAsTemplate = async (name: string, description: string, category: TemplateCategory, tags: string[]) => {
    if (!showSaveAsTemplate) return;
    try {
      await saveAsTemplate(showSaveAsTemplate.surveyId, name, description, category, tags);
      setShowSaveAsTemplate(null);
      // Reload templates
      const updatedTemplates = await listTemplates();
      setTemplates(updatedTemplates);
      alert('Survey saved as template successfully!');
    } catch (err: any) {
      alert(`Failed to save as template: ${err.message}`);
    }
  };

  const handleTemplateCategoryChange = async (template: SurveyTemplate, category: TemplateCategory) => {
    try {
      await updateTemplate(template.id, { category });
      setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, category } : t));
    } catch (err: any) {
      alert(`Failed to update category: ${err.message}`);
    }
  };

  const handleDuplicateTemplate = async (template: SurveyTemplate) => {
    try {
      const { id, createdAt, updatedAt, isSystemTemplate, ...rest } = template;
      await createTemplate({
        ...rest,
        name: `${template.name} (Copy)`,
        isSystemTemplate: false,
      });
      const updated = await listTemplates();
      setTemplates(updated);
    } catch (err: any) {
      alert(`Failed to duplicate template: ${err.message}`);
    }
  };

  const handleDeleteTemplate = async (template: SurveyTemplate) => {
    if (!window.confirm(`Are you sure you want to delete the template "${template.name}"?`)) return;
    try {
      await deleteTemplate(template.id);
      setTemplates(prev => prev.filter(t => t.id !== template.id));
    } catch (err: any) {
      alert(`Failed to delete template: ${err.message}`);
    }
  };

  const handleEditTemplate = (template: SurveyTemplate) => {
    // Convert template into a partial SurveyDefinition for the builder
    const surveyFromTemplate: Partial<SurveyDefinition> = {
      title: template.name,
      description: template.description,
      sections: template.sections,
      settings: {
        isActive: false,
        allowAnonymous: template.defaultSettings?.allowAnonymous ?? true,
        requireAuth: false,
        allowMultipleResponses: false,
        showProgressBar: template.defaultSettings?.showProgressBar ?? true,
        collectEmail: template.defaultSettings?.collectEmail ?? false,
        collectIpAddress: false,
        accessType: 'public',
        thankYouMessage: template.defaultSettings?.thankYouMessage || 'Thank you for completing this survey!',
      },
    };

    setEditingTemplate(template);
    setDashboardState(prev => ({
      ...prev,
      activeSection: 'builder',
      editingSurvey: surveyFromTemplate,
      showSurveyBuilder: true,
    }));
    updateURLParams('builder');
  };

  const handleSaveTemplate = async (survey: Partial<SurveyDefinition>, options?: { silent?: boolean }): Promise<string | void> => {
    if (!editingTemplate) return;
    try {
      await updateTemplate(editingTemplate.id, {
        name: survey.title || editingTemplate.name,
        description: survey.description || '',
        sections: survey.sections || [],
        defaultSettings: {
          allowAnonymous: survey.settings?.allowAnonymous ?? true,
          showProgressBar: survey.settings?.showProgressBar ?? true,
          collectEmail: survey.settings?.collectEmail ?? false,
          thankYouMessage: survey.settings?.thankYouMessage,
        },
      });

      if (!options?.silent) {
        alert('Template updated successfully!');
        // Reload templates and close builder
        const updatedTemplates = await listTemplates();
        setTemplates(updatedTemplates);
        setEditingTemplate(null);
        setDashboardState(prev => ({
          ...prev,
          activeSection: 'templates',
          showSurveyBuilder: false,
          editingSurvey: null,
        }));
        updateURLParams('templates');
      }
    } catch (err: any) {
      if (!options?.silent) {
        alert(`Failed to update template: ${err.message}`);
      }
    }
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !templateSearch ||
      t.name.toLowerCase().includes(templateSearch.toLowerCase()) ||
      t.description.toLowerCase().includes(templateSearch.toLowerCase());
    const matchesCategory = templateCategoryFilter === 'all' || t.category === templateCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categoryFilters: { value: TemplateCategory | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'patient_feedback', label: 'Patient Feedback' },
    { value: 'clinical_research', label: 'Clinical Research' },
    { value: 'product_evaluation', label: 'Product Evaluation' },
    { value: 'general', label: 'General' },
    { value: 'custom', label: 'Custom' },
  ];

  const renderTemplates = () => (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex items-center justify-between gap-4">
        {/* Search */}
        <div className="flex-1 max-w-sm">
          <input
            type="text"
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
            placeholder="Search templates..."
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--glass-border-light)' }}
          />
        </div>
      </div>

      {/* Category filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {categoryFilters.map(cat => (
          <button
            key={cat.value}
            onClick={() => setTemplateCategoryFilter(cat.value)}
            className="px-3 py-1 rounded-full text-xs font-medium transition-colors"
            style={{
              backgroundColor: templateCategoryFilter === cat.value ? 'var(--color-primary)' : 'var(--glass-bg-card)',
              color: templateCategoryFilter === cat.value ? 'white' : 'var(--color-muted)',
              border: `1px solid ${templateCategoryFilter === cat.value ? 'var(--color-primary)' : 'var(--glass-border-light)'}`,
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Templates list */}
      {filteredTemplates.length === 0 ? (
        <div className="glass-card p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'var(--primary-50)' }}>
              <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-primary)' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>
              {templates.length === 0 ? 'No templates yet' : 'No matching templates'}
            </h3>
            <p className="mb-6 leading-relaxed" style={{ color: 'var(--color-muted)' }}>
              {templates.length === 0
                ? 'Save any survey as a template, or load the starter template to get going.'
                : 'Try adjusting your search or category filter.'}
            </p>
            {templates.length === 0 && isSuperAdmin && (
              <button
                onClick={async () => {
                  try {
                    await seedSystemTemplates();
                    const updated = await listTemplates();
                    setTemplates(updated);
                  } catch (err: any) {
                    alert(`Failed to seed templates: ${err.message}`);
                  }
                }}
                className="glass-btn-primary"
                disabled={templatesLoading}
              >
                {templatesLoading ? 'Loading...' : 'Load Starter Templates'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onUseTemplate={handleUseTemplate}
              onPreview={(t) => setShowTemplatePreview(t)}
              onEdit={handleEditTemplate}
              onDuplicate={handleDuplicateTemplate}
              onCategoryChange={handleTemplateCategoryChange}
              onDelete={handleDeleteTemplate}
              canDelete={!template.isSystemTemplate || isSuperAdmin}
            />
          ))}
        </div>
      )}

      {/* Summary + Reseed */}
      <div className="flex items-center justify-center gap-3 pt-2">
        {templates.length > 0 && (
          <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {filteredTemplates.length} of {templates.length} templates
          </span>
        )}
        {isSuperAdmin && (
          <button
            onClick={async () => {
              try {
                const result = await seedSystemTemplates();
                const updated = await listTemplates();
                setTemplates(updated);
                alert(typeof result === 'string' ? result : 'System templates synced successfully');
              } catch (err: any) {
                alert(`Failed to seed templates: ${err.message}`);
              }
            }}
            className="text-xs px-2.5 py-1 rounded transition-colors"
            style={{
              backgroundColor: 'rgba(38, 67, 114, 0.05)',
              color: '#6a8caf',
              border: '1px solid rgba(38, 67, 114, 0.1)',
            }}
            disabled={templatesLoading}
          >
            {templatesLoading ? 'Syncing...' : 'Sync System Templates'}
          </button>
        )}
      </div>
    </div>
  );

  const renderSurveyBuilder = () => {
    if (dashboardState.showSurveyBuilder) {
      return (
        <SurveyBuilder
          ref={surveyBuilderRef}
          survey={dashboardState.editingSurvey || undefined}
          onSave={editingTemplate ? handleSaveTemplate : handleSaveSurvey}
          onCancel={handleCancelSurveyBuilder}
        />
      );
    }

    return (
      <div className="space-y-6">
        {/* Action Button */}
        <div className="flex items-center justify-end">
          <button
            onClick={startNewSurvey}
            className="glass-btn-primary flex items-center gap-2"
          >
            <span>Create New Survey</span>
          </button>
        </div>

        {/* Start from Template */}
        {templates.length > 0 && (
          <div className="glass-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-sm font-semibold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>
                  Start from Template
                </h3>
                <p className="text-xs" style={{ color: 'var(--color-muted)' }}>
                  Choose a template to get started quickly
                </p>
              </div>
              <button
                onClick={() => {
                  setDashboardState(prev => ({ ...prev, activeSection: 'templates' }));
                  updateURLParams('templates');
                }}
                className="text-xs font-medium hover:underline"
                style={{ color: 'var(--color-accent)' }}
              >
                Browse All Templates
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {templates.slice(0, 3).map(template => (
                <button
                  key={template.id}
                  onClick={() => handleUseTemplate(template)}
                  className="text-left p-3 rounded-xl border transition-all hover:shadow-sm"
                  style={{
                    borderColor: 'var(--glass-border-light)',
                    backgroundColor: 'var(--glass-bg-card)',
                  }}
                >
                  <h4 className="text-sm font-medium truncate" style={{ color: 'var(--color-primary)' }}>
                    {template.name}
                  </h4>
                  <p className="text-xs mt-1 truncate" style={{ color: 'var(--color-muted)' }}>
                    {template.sectionCount} sections, {template.questionCount} questions
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Surveys Grid */}
        {dashboardState.surveys.length === 0 ? (
          <div className="glass-card p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'var(--primary-50)' }}>
                <span className="text-4xl">📝</span>
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>Ready to build your first survey?</h3>
              <p className="mb-8 leading-relaxed" style={{ color: 'var(--color-muted)' }}>
                Create engaging surveys with our intuitive drag-and-drop builder. Add multiple question types,
                sections, and customize the experience for your respondents.
              </p>
              <button
                onClick={startNewSurvey}
                className="glass-btn-primary glass-btn-lg"
              >
                Create Your First Survey
              </button>

              <div className="mt-8 grid grid-cols-2 gap-4 text-sm" style={{ color: 'var(--color-muted)' }}>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-primary)' }}></div>
                  <span>Drag & Drop Builder</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-success)' }}></div>
                  <span>Multiple Question Types</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-accent)' }}></div>
                  <span>QR Code Generation</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--status-warning)' }}></div>
                  <span>Real-time Analytics</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {dashboardState.surveys.map((survey) => (
              <div key={survey.id} className="glass-card">
                <div className="flex items-start justify-between gap-6 p-5">
                  {/* Left: Title & Description */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-primary)' }}>
                        {survey.title}
                      </h3>
                      <span className={`glass-badge ${survey.isArchived ? 'glass-badge-info' : survey.settings?.isActive ? 'glass-badge-success' : 'glass-badge-info'}`}>
                        {survey.isArchived ? 'Archived' : survey.settings?.isActive ? 'Active' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-sm text-muted line-clamp-1">
                      {survey.description ? stripHTML(survey.description) || "No description provided" : "No description provided"}
                    </p>
                  </div>

                  {/* Middle: Stats - Inline */}
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <div className="text-center">
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.5rem', fontWeight: 600, color: 'var(--color-primary)', lineHeight: '1' }}>
                        {survey.sections?.length || 0}
                      </p>
                      <p className="text-xs text-muted mt-1">Sections</p>
                    </div>
                    <div className="text-center">
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.5rem', fontWeight: 600, color: 'var(--color-accent)', lineHeight: '1' }}>
                        {survey.sections?.reduce((total, section) => total + section.questions.length, 0) || 0}
                      </p>
                      <p className="text-xs text-muted mt-1">Questions</p>
                    </div>
                    <div className="text-center">
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: '1.5rem', fontWeight: 600, color: 'var(--color-primary)', lineHeight: '1' }}>
                        {survey.analytics?.totalResponses || 0}
                      </p>
                      <p className="text-xs text-muted mt-1">Responses</p>
                    </div>
                  </div>

                  {/* Right: Action Buttons - Stacked Vertically */}
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      onClick={() => editSurvey(survey)}
                      className="glass-btn-accent-soft glass-btn-sm"
                      style={{ minWidth: '110px' }}
                      title="Edit Survey"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => generateQRCode(survey.id)}
                      className="glass-btn-secondary glass-btn-sm"
                      style={{ minWidth: '110px' }}
                      title="Generate QR Code"
                    >
                      QR Code
                    </button>
                    <button
                      onClick={() => handleDuplicateSurvey(survey.id, survey.title)}
                      className="glass-btn-secondary glass-btn-sm"
                      style={{ minWidth: '110px' }}
                      title="Duplicate Survey"
                    >
                      Duplicate
                    </button>
                    <button
                      onClick={() => setShowSaveAsTemplate({ surveyId: survey.id, title: survey.title, description: survey.description })}
                      className="glass-btn-secondary glass-btn-sm"
                      style={{ minWidth: '110px' }}
                      title="Save as Template"
                    >
                      Save Template
                    </button>
                    <button
                      onClick={() => handleDeleteSurveyClick(survey.id, survey.title)}
                      className="glass-btn-danger-soft glass-btn-sm"
                      style={{ minWidth: '110px' }}
                      title="Delete Survey"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;

    setPasswordResetLoading(true);
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, user.email, {
        url: `${window.location.origin}/admin`,
        handleCodeInApp: false,
      });
      setPasswordResetSent(true);
      setTimeout(() => setPasswordResetSent(false), 5000);
    } catch (error: any) {
      console.error('Error sending password reset:', error);
      alert('Failed to send password reset email. Please try again.');
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handleBackfillLaunchedAt = async () => {
    setBackfillLoading(true);
    setBackfillError(null);
    setBackfillResult(null);
    try {
      const fn = httpsCallable<
        Record<string, never>,
        APIResponse<{ updated: number; scanned: number }>
      >(functions, 'backfillLaunchedAt');
      const res = await fn({});
      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.error || 'Backfill failed');
      }
      setBackfillResult(res.data.data);
      // Refresh surveys so analytics shows the new launchedAt immediately
      await loadDashboardData();
    } catch (err: any) {
      console.error('Backfill error:', err);
      setBackfillError(err.message || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  const renderSettings = () => {
    return (
      <div className="space-y-6">
        {/* Account Information */}
        <div className="glass-card-accent overflow-hidden">
          <div className="px-6 py-4" style={{ borderBottom: '3px solid var(--color-accent)' }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'var(--font-size-h3)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Account Information
            </h3>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="glass-label">Display Name</label>
              <div className="text-base font-medium" style={{ color: 'var(--color-primary-ink)' }}>
                {profile?.displayName || 'Not set'}
              </div>
            </div>

            <div>
              <label className="glass-label">Email Address</label>
              <div className="text-base font-medium" style={{ color: 'var(--color-primary-ink)' }}>
                {user?.email}
              </div>
            </div>

            <div>
              <label className="glass-label">Account Role</label>
              <div className={`glass-badge ${profile?.role === 'superAdmin' ? 'glass-badge-accent' : 'glass-badge-primary'}`}>
                {profile?.role === 'superAdmin' ? 'Super Administrator' :
                 profile?.role === 'admin' ? 'Administrator' :
                 'Viewer'}
              </div>
            </div>

            <div>
              <label className="glass-label">Account Status</label>
              <div className={`glass-badge ${profile?.isActive ? 'glass-badge-success' : 'glass-badge-error'}`}>
                {profile?.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
          </div>
        </div>

        {/* Security Settings */}
        <div className="glass-card-accent overflow-hidden">
          <div className="px-6 py-4" style={{ borderBottom: '3px solid var(--color-accent)' }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'var(--font-size-h3)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Security
            </h3>
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold mb-1" style={{ color: 'var(--color-primary)' }}>Change Password</h4>
                <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                  We'll send a password reset link to your email address
                </p>
                {passwordResetSent && (
                  <div className="mt-2 flex items-center text-sm font-medium" style={{ color: 'var(--color-success)' }}>
                    <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Password reset email sent! Check your inbox.
                  </div>
                )}
              </div>
              <button
                onClick={handlePasswordReset}
                disabled={passwordResetLoading || passwordResetSent}
                className={passwordResetSent ? 'glass-btn-secondary glass-btn-sm' : 'glass-btn-accent glass-btn-sm'}
              >
                {passwordResetLoading ? 'Sending...' : passwordResetSent ? 'Email Sent' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>

        {/* Session Information */}
        <div className="glass-card-accent overflow-hidden">
          <div className="px-6 py-4" style={{ borderBottom: '3px solid var(--color-accent)' }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'var(--font-size-h3)',
              color: 'var(--color-primary)',
              margin: 0
            }}>
              Session Information
            </h3>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--glass-border-light)' }}>
              <span className="text-sm" style={{ color: 'var(--color-muted)' }}>Email Verified</span>
              <span className={`glass-badge ${user?.emailVerified ? 'glass-badge-success' : 'glass-badge-error'}`}>
                {user?.emailVerified ? 'Verified' : 'Not Verified'}
              </span>
            </div>

            <div className="flex items-center justify-between py-2" style={{ borderBottom: '1px solid var(--glass-border-light)' }}>
              <span className="text-sm" style={{ color: 'var(--color-muted)' }}>Last Login</span>
              <span className="text-sm font-medium" style={{ color: 'var(--color-primary-ink)' }}>
                {profile?.lastLogin
                  ? new Date(profile.lastLogin.seconds * 1000).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  : 'N/A'}
              </span>
            </div>

            <div className="flex items-center justify-between py-2">
              <span className="text-sm" style={{ color: 'var(--color-muted)' }}>Account Created</span>
              <span className="text-sm font-medium" style={{ color: 'var(--color-primary-ink)' }}>
                {profile?.createdAt
                  ? new Date(profile.createdAt.seconds * 1000).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* Maintenance — superAdmin only. Idempotent one-shot to backfill launchedAt
            on surveys that were already active before the lifecycle feature shipped. */}
        {isSuperAdmin && (
          <div className="glass-card-accent overflow-hidden">
            <div className="px-6 py-4" style={{ borderBottom: '3px solid var(--color-accent)' }}>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'var(--font-size-h3)',
                color: 'var(--color-primary)',
                margin: 0
              }}>
                Maintenance
              </h3>
            </div>
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h4 className="font-semibold mb-1" style={{ color: 'var(--color-primary)' }}>
                    Backfill Launch Dates
                  </h4>
                  <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
                    For surveys that were already active before launch-date tracking shipped, set
                    <code className="mx-1 px-1 rounded" style={{ backgroundColor: 'rgba(38, 67, 114, 0.08)', fontSize: '0.8125rem' }}>launchedAt</code>
                    to their original creation date. Idempotent — safe to re-run.
                  </p>
                  {backfillResult && (
                    <div
                      className="mt-2 text-sm font-medium"
                      style={{ color: 'var(--color-success, #2f7a4f)' }}
                    >
                      Backfill complete: {backfillResult.updated} of {backfillResult.scanned} active surveys updated.
                    </div>
                  )}
                  {backfillError && (
                    <div className="mt-2 text-sm font-medium" style={{ color: 'var(--color-error, #b1442a)' }}>
                      {backfillError}
                    </div>
                  )}
                </div>
                <button
                  onClick={handleBackfillLaunchedAt}
                  disabled={backfillLoading}
                  className="glass-btn-accent glass-btn-sm"
                >
                  {backfillLoading ? 'Backfilling…' : 'Run Backfill'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--color-surface)' }}>
      {/* Sidebar - Crisci Branded */}
      <div className="w-60 flex flex-col" style={{ backgroundColor: 'var(--glass-bg-card)', borderRight: '1px solid var(--glass-border-light)' }}>
        {/* Logo / Brand */}
        <div className="p-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'var(--font-size-h3)', color: 'var(--color-primary)' }}>
            Crisci Create
          </h1>
          <p className="text-xs text-muted">Admin Dashboard</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {/* Home Button */}
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center px-3 py-2.5 text-left rounded-lg transition-colors hover:bg-gray-50 mb-2"
            style={{ color: 'var(--color-primary-ink)' }}
          >
            <span className="mr-2.5 flex-shrink-0" style={{ color: 'var(--color-muted)' }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </span>
            <div className="flex-1 min-w-0">
              <div className="truncate" style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '0.9375rem',
                fontWeight: 400
              }}>
                Home
              </div>
            </div>
          </button>

          <div className="border-b mb-2" style={{ borderColor: 'var(--glass-border-light)' }}></div>

          {navigationSections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                setDashboardState(prev => ({
                  ...prev,
                  activeSection: section.id as DashboardState['activeSection'],
                  selectedSurveyForDetails: null,
                  detailsTab: null
                }));
                updateURLParams(section.id);
              }}
              className={`w-full flex items-center px-3 py-2.5 text-left rounded-lg transition-colors ${
                dashboardState.activeSection === section.id
                  ? 'border-l-4'
                  : 'hover:bg-gray-50'
              }`}
              style={dashboardState.activeSection === section.id ? {
                backgroundColor: 'rgba(199, 161, 74, 0.1)',
                borderLeftColor: 'var(--color-accent)',
                color: 'var(--color-primary)'
              } : {
                color: 'var(--color-primary-ink)'
              }}
            >
              <span className="mr-2.5 flex-shrink-0" style={{ color: dashboardState.activeSection === section.id ? 'var(--color-accent)' : 'var(--color-muted)' }}>{section.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="truncate" style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '0.9375rem',
                  fontWeight: dashboardState.activeSection === section.id ? 600 : 400
                }}>
                  {section.name}
                </div>
              </div>
            </button>
          ))}

          {/* External Navigation - Studies */}
          {(isAdmin || isSuperAdmin) && (
            <button
              onClick={() => navigate('/studies')}
              className="w-full flex items-center px-3 py-2.5 text-left rounded-lg transition-colors hover:bg-gray-50 mt-2 border-t border-gray-100 pt-3"
              style={{ color: 'var(--color-primary-ink)' }}
            >
              <span className="mr-2.5 flex-shrink-0" style={{ color: 'var(--color-muted)' }}>{NavIcons.studies}</span>
              <div className="flex-1 min-w-0">
                <div className="truncate" style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: '0.9375rem',
                  fontWeight: 400
                }}>
                  Studies
                </div>
              </div>
            </button>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center mb-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isSuperAdmin ? 'bg-red-500' : isAdmin ? 'bg-blue-500' : 'bg-gray-500'
            }`}>
              <span className="text-white text-sm font-medium">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {profile?.displayName || user?.email?.split('@')[0] || 'User'}
              </p>
              <p className="text-xs text-gray-500 capitalize">
                {profile?.role === 'superAdmin' ? 'Super Admin' : profile?.role || 'User'}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Header */}
        <header className="shadow-sm px-6" style={{ backgroundColor: '#ebebeb', borderBottom: '1px solid var(--color-border)', paddingTop: '0.625rem', paddingBottom: '0.625rem' }}>
          <div className="flex justify-between items-center">
            <div>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '0.125rem' }}>
                {editingTemplate
                  ? `Editing Template: ${editingTemplate.name}`
                  : navigationSections.find(s => s.id === dashboardState.activeSection)?.name || 'Dashboard'}
              </h2>
              <p className="text-sm text-muted" style={{ fontFamily: 'var(--font-body)' }}>
                {editingTemplate
                  ? 'Changes will be saved to the template, not as a survey'
                  : navigationSections.find(s => s.id === dashboardState.activeSection)?.description || ''}
              </p>
            </div>

            {/* Show action buttons when in Survey Builder and builder is open */}
            {dashboardState.activeSection === 'builder' && dashboardState.showSurveyBuilder && (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelSurveyBuilder}
                  className="glass-btn-secondary glass-btn-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => surveyBuilderRef.current?.save()}
                  className="glass-btn-accent glass-btn-sm"
                >
                  {editingTemplate ? 'Save Template' : 'Save Survey'}
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className={`flex-1 overflow-y-auto ${dashboardState.activeSection === 'builder' && dashboardState.showSurveyBuilder ? 'p-0' : 'p-6'}`}>
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="glass-card text-center" style={{ padding: '2rem' }}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: 'var(--color-accent)' }}></div>
                <p className="mt-4" style={{ color: 'var(--color-muted)' }}>Loading...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="glass-card mb-6" style={{ borderLeft: '4px solid var(--status-error)' }}>
              <div className="flex p-4">
                <div className="mr-3" style={{ color: 'var(--status-error)' }}>❌</div>
                <div>
                  <h3 className="font-medium" style={{ color: 'var(--status-error)' }}>Error</h3>
                  <p style={{ color: 'var(--color-muted)' }}>{error}</p>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && (
            <>
              {dashboardState.activeSection === 'overview' && renderOverview()}
              {dashboardState.activeSection === 'surveys' && renderSurveys()}
              {dashboardState.activeSection === 'builder' && renderSurveyBuilder()}
              {dashboardState.activeSection === 'templates' && renderTemplates()}

              {dashboardState.activeSection === 'users' && (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
                    <div className="text-6xl mb-4">👥</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">User Management</h3>
                    <p className="text-gray-600 mb-6">
                      Manage admin users, roles, and permissions from the dedicated user management interface.
                    </p>
                    <button
                      onClick={() => navigate('/admin/users')}
                      className="btn-primary"
                    >
                      Go to User Management
                    </button>
                  </div>
                </div>
              )}
              {dashboardState.activeSection === 'settings' && renderSettings()}
            </>
          )}
        </main>
      </div>

      {/* QR Code Modal - Enhanced with Access Code */}
      {dashboardState.showQRModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl p-4 sm:p-6 max-w-lg w-full my-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Close button - Always visible at top */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Survey Access</h3>
              <button
                onClick={() => setDashboardState(prev => ({ ...prev, showQRModal: false, qrCodeUrl: '', activeSurvey: null }))}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="text-center">

              {/* QR Code Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3">SCAN QR CODE</p>
                {dashboardState.qrCodeUrl && (
                  <div className="bg-white p-3 sm:p-4 rounded-lg inline-block shadow-md">
                    <img
                      src={dashboardState.qrCodeUrl}
                      alt="Survey QR Code"
                      className="mx-auto max-w-[200px] sm:max-w-full w-full h-auto"
                    />
                  </div>
                )}
              </div>

              {/* Access Code Section */}
              {dashboardState.activeSurvey?.accessCode && (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-3">OR ENTER ACCESS CODE</p>
                  <div className="bg-white px-4 py-3 sm:px-6 sm:py-4 rounded-lg inline-block shadow-md">
                    <div className="text-3xl sm:text-4xl md:text-5xl font-black text-green-600 tracking-wider font-mono">
                      {dashboardState.activeSurvey.accessCode}
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-3">
                    Participants can enter this code on the homepage
                  </p>
                </div>
              )}

              {/* Instructions */}
              <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                Share the QR code or access code to allow participants to access the survey
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => {
                    if (dashboardState.qrCodeUrl) {
                      const link = document.createElement('a');
                      link.href = dashboardState.qrCodeUrl;
                      link.download = `survey-qr-${dashboardState.activeSurvey?.accessCode || 'code'}.png`;
                      link.click();
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 sm:px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg text-sm sm:text-base"
                >
                  Download QR
                </button>
                <button
                  onClick={() => setDashboardState(prev => ({ ...prev, showQRModal: false, qrCodeUrl: '', activeSurvey: null }))}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 sm:px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all text-sm sm:text-base"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Export Modal */}
      {showPDFExportModal && dashboardState.selectedSurveyForDetails && (
        <PDFExportModal
          isOpen={showPDFExportModal}
          onClose={() => setShowPDFExportModal(false)}
          onExport={handlePDFExport}
          survey={dashboardState.selectedSurveyForDetails}
          totalResponses={dashboardState.selectedSurveyForDetails.analytics?.totalResponses || 0}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={dashboardState.deleteConfirmation.isOpen}
        title="Delete Survey"
        message={`Are you sure you want to delete "${dashboardState.deleteConfirmation.surveyTitle}"? This action cannot be undone and will permanently delete all survey data and responses.`}
        confirmText="Delete Survey"
        cancelText="Cancel"
        onConfirm={handleDeleteSurveyConfirm}
        onCancel={handleDeleteSurveyCancel}
        type="danger"
      />

      {/* Template Modals */}
      {showCreateFromTemplate && (
        <CreateFromTemplateModal
          template={showCreateFromTemplate}
          onCreate={handleCreateFromTemplate}
          onClose={() => setShowCreateFromTemplate(null)}
          loading={templatesLoading}
        />
      )}

      {showSaveAsTemplate && (
        <SaveAsTemplateModal
          surveyTitle={showSaveAsTemplate.title}
          surveyDescription={showSaveAsTemplate.description}
          onSave={handleSaveAsTemplate}
          onClose={() => setShowSaveAsTemplate(null)}
          loading={templatesLoading}
        />
      )}

      {showTemplatePreview && (
        <TemplatePreviewModal
          template={showTemplatePreview}
          onClose={() => setShowTemplatePreview(null)}
          onUseTemplate={handleUseTemplate}
        />
      )}

      {/* Manage Tags Modal */}
      {managingTags && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-primary)' }}>
                Manage Tags
              </h3>
              <button
                onClick={() => setManagingTags(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
              {managingTags.surveyTitle}
            </p>

            {/* Current tags */}
            {managingTags.currentTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {managingTags.currentTags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                    style={{
                      backgroundColor: 'rgba(38, 67, 114, 0.08)',
                      color: '#264372',
                      border: '1px solid rgba(38, 67, 114, 0.15)',
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => setManagingTags(prev => prev ? {
                        ...prev,
                        currentTags: prev.currentTags.filter(t => t !== tag)
                      } : null)}
                      className="hover:text-red-500 transition-colors ml-0.5"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Tag input */}
            <div className="mb-3">
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: 'var(--glass-border-light)' }}
                placeholder="Type a tag and press Enter (or comma-separated)"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    const input = (e.target as HTMLInputElement);
                    const newTags = input.value.split(',').map(t => t.trim()).filter(t => t.length > 0);
                    if (newTags.length > 0) {
                      setManagingTags(prev => prev ? {
                        ...prev,
                        currentTags: Array.from(new Set([...prev.currentTags, ...newTags]))
                      } : null);
                      input.value = '';
                    }
                  }
                }}
              />
            </div>

            {/* Suggested tags from existing surveys */}
            {allTags.filter(t => !managingTags.currentTags.includes(t)).length > 0 && (
              <div className="mb-4">
                <p className="text-xs mb-1.5" style={{ color: 'var(--color-muted)' }}>Existing tags:</p>
                <div className="flex flex-wrap gap-1">
                  {allTags.filter(t => !managingTags.currentTags.includes(t)).map(tag => (
                    <button
                      key={tag}
                      onClick={() => setManagingTags(prev => prev ? {
                        ...prev,
                        currentTags: [...prev.currentTags, tag]
                      } : null)}
                      className="text-xs px-2 py-0.5 rounded-full transition-colors hover:bg-gray-100"
                      style={{
                        backgroundColor: 'rgba(38, 67, 114, 0.04)',
                        color: 'var(--color-muted)',
                        border: '1px solid rgba(38, 67, 114, 0.1)',
                      }}
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setManagingTags(null)}
                className="glass-btn-secondary glass-btn-sm"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await updateSurvey(managingTags.surveyId, { tags: managingTags.currentTags } as any);
                    await loadDashboardData();
                    setManagingTags(null);
                  } catch (err: any) {
                    alert(`Failed to update tags: ${err.message}`);
                  }
                }}
                className="glass-btn-primary glass-btn-sm"
              >
                Save Tags
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;